import { FC, FormEvent, useEffect } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { WritebackSettingMode } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { CallIntelligenceWritebackV1 } from '@frontend/api-call-intel';
import { checkForPMSSyncManageACL } from '@frontend/call-intel-core';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, useForm } from '@frontend/design-system';
import { checkAutomaticSyncEligible } from '../../utils';
import { UnauthorizedAccess } from '../unauthorized-access';
import { CallSummaryPmsSyncSettings } from './call-summary-pms-sync-settings';

interface CallSummaryPmsSyncSettingsFormProps {
  locationId: string;
  containerStyles?: Interpolation<Theme>;
  syncSettingCardStyles?: Interpolation<Theme>;
  value?: CallIntelligenceWritebackV1.Types.WritebackSettingMode;
  isLoading?: boolean;
  onSave?: (data: { locationId: string; mode: CallIntelligenceWritebackV1.Types.WritebackSettingMode }) => void;
}

export const CallSummaryPmsSyncSettingsForm: FC<CallSummaryPmsSyncSettingsFormProps> = ({
  locationId,
  containerStyles,
  syncSettingCardStyles,
  isLoading,
  value = CallIntelligenceWritebackV1.Types.WritebackSettingMode.WRITEBACK_SETTING_MODE_UNKNOWN,
  onSave,
}) => {
  const { t } = useTranslation('settings', { keyPrefix: 'call-intel-settings' });
  const { accessibleLocationData } = useAppScopeStore();
  const {
    getFieldProps,
    values: formValues,
    seedValues,
  } = useForm({
    fields: {
      syncOption: {
        type: 'radio',
        value,
      },
    },
  });

  useEffect(() => {
    if (value && value !== formValues.syncOption) {
      seedValues({ syncOption: value });
    }
  }, [value]);

  const hasPermission = checkForPMSSyncManageACL({ locationId });

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (formValues.syncOption) {
      onSave?.({ locationId, mode: formValues.syncOption as WritebackSettingMode });
    }
  };

  const shouldHideAutomaticSyncOption = !checkAutomaticSyncEligible({
    vertical: accessibleLocationData[locationId]?.vertical,
  });

  if (!hasPermission) {
    return <UnauthorizedAccess containerStyles={containerStyles} />;
  }

  return (
    <form css={[styles.container, containerStyles]} onSubmit={handleFormSubmit}>
      <ContentLoader show={isLoading} />
      <CallSummaryPmsSyncSettings
        fieldProps={getFieldProps('syncOption')}
        containerStyles={[styles.syncSettings, syncSettingCardStyles]}
        hideAutomaticSyncOption={shouldHideAutomaticSyncOption}
      />
      <Divider margin={0} color={theme.colors.neutral20} />
      <div css={styles.action}>
        <Button type='submit' variant='primary' disabled={isLoading || value === formValues.syncOption}>
          {t('Save')}
        </Button>
      </div>
    </form>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    position: relative;
  `,
  unauthorizedContainer: css`
    align-items: center;
    padding: ${theme.spacing(3)};
  `,
  syncSettings: css`
    margin: ${theme.spacing(3)};
    padding: ${theme.spacing(3)};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.medium};
  `,
  action: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: ${theme.spacing(3)};
  `,
};
